import {
  Settings as SettingsIcon,
  Add as AddIcon,
  WhatsApp,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Email,
} from "@mui/icons-material";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Popover,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import GenericTable from "0.Mine/0.Components/Reusable/GenericTable";
import { useClients, useCustomFields } from "0.Mine/1.Hooks/utils/clients";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Client } from "0.Mine/3.Interfaces/Client";

import ComposeEmailModal from "../ComposeEmailModal";

import ClientImportModal from "./AddBulkV2/ClientImportModalV2";
import { AddClientModal } from "./AddClientModal";
import ClientSearch from "./ClientSearch";
import { CustomFieldsManager } from "./CustomFieldManagement";
import { EditClientModal } from "./EditClient";
import ClientNotesModal from "./NotesDialogV2";

// Define interface for filter object
interface FilterOption {
  value: any;
  enabled: boolean;
  operator?: "contains" | "equals" | "greaterThan" | "lessThan" | "between";
  secondValue?: any; // For between operator
}

interface FilterState {
  quickSearch: string;
  advancedFilters: {
    name?: FilterOption;
    email?: FilterOption;
    phone?: FilterOption;
    source?: FilterOption;
    customer_group?: FilterOption;
    total_spent?: FilterOption;
    created?: FilterOption;
    updated?: FilterOption;
    last_login?: FilterOption;
    tags?: FilterOption;
  };
  showAdvanced: boolean;
}

// Default filter state
const defaultFilterState: FilterState = {
  quickSearch: "",
  advancedFilters: {
    name: { value: "", enabled: false, operator: "contains" },
    email: { value: "", enabled: false, operator: "contains" },
    phone: { value: "", enabled: false, operator: "contains" },
    source: { value: "", enabled: false, operator: "equals" },
    customer_group: { value: "", enabled: false, operator: "equals" },
    total_spent: { value: "", enabled: false, operator: "greaterThan", secondValue: "" },
    created: { value: null, enabled: false, operator: "between", secondValue: null },
    updated: { value: null, enabled: false, operator: "between", secondValue: null },
    last_login: { value: null, enabled: false, operator: "between", secondValue: null },
    tags: { value: "", enabled: false },
  },
  showAdvanced: false,
};

export function ClientTable() {
  const navigate = useNavigate();
  const initialLoadRef = useRef(false);
  const [filters, setFilters] = useState<typeof defaultFilterState>(defaultFilterState);

  // Remove unused refs and state
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [customFieldsModalOpen, setCustomFieldsModalOpen] = useState<boolean>(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [bulkImportModalOpen, setBulkImportModalOpen] = useState<boolean>(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>("0");

  const {
    clients,
    isLoading: clientsLoading,
    pagination,
    loadClients,
    addClient,
    updateClient,
    deleteClient,
  } = useClients();

  const {
    customFields,
    isLoading: customFieldsLoading,
    addCustomField,
    updateCustomField,
    deleteCustomField,
    refetch,
  } = useCustomFields();

  // Add businessContext
  const { session } = useBusinessContext();

  // Initial load - only runs once on mount and when session changes
  useEffect(() => {
    if (session?.selectedBusiness?.id && !initialLoadRef.current) {
      initialLoadRef.current = true;
      loadClients(1, pagination.pageSize);
    }
  }, [session?.selectedBusiness?.id]);

  // Memoized search handler
  const handleSearch = useCallback(
    async (searchTerm: string) => {
      setFilters((prev) => ({ ...prev, quickSearch: searchTerm }));
      await loadClients(1, pagination.pageSize, searchTerm || undefined);
    },
    [pagination.pageSize, loadClients]
  );

  // Handle pagination change
  const handlePageChange = useCallback(
    (newPage: number) => {
      // Don't set searching state if we're already on this page
      if (newPage === pagination.currentPage) {
        return;
      }

      loadClients(newPage, pagination.pageSize, filters.quickSearch);
    },
    [filters.quickSearch, pagination.currentPage, pagination.pageSize, loadClients]
  );

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback(
    (newPageSize: number) => {
      // Don't reload if the page size hasn't changed
      if (newPageSize === pagination.pageSize) {
        return;
      }

      loadClients(1, newPageSize, filters.quickSearch);
    },
    [filters.quickSearch, pagination.pageSize, loadClients]
  );

  // Memoize the pagination object to prevent unnecessary re-renders
  const paginationConfig = useMemo(
    () => ({
      page: pagination.currentPage - 1,
      pageSize: pagination.pageSize,
      total: pagination.total,
      onPageChange: (newPage: number) => handlePageChange(newPage + 1),
      onRowsPerPageChange: handleRowsPerPageChange,
    }),
    [
      pagination.currentPage,
      pagination.pageSize,
      pagination.total,
      handlePageChange,
      handleRowsPerPageChange,
    ]
  );

  // Initialize advanced filters based on customFields
  useEffect(() => {
    if (customFields.length > 0) {
      setFilters((prev) => {
        const hasAllStandard = [
          "name",
          "email",
          "phone",
          "company",
          "status",
          "value",
          "tags",
        ].every((key) => !!prev.advancedFilters[key]);
        const hasAllCustom = customFields.every((field) => !!prev.advancedFilters[field.id]);

        if (hasAllStandard && hasAllCustom) {
          return prev;
        }

        const advanced = { ...prev.advancedFilters };

        ["name", "email", "phone", "company", "status", "value", "tags"].forEach((key) => {
          if (!advanced[key]) {
            advanced[key] = { value: "", enabled: false, operator: "contains" };
          }
        });

        customFields.forEach((field) => {
          if (!advanced[field.id]) {
            advanced[field.id] = {
              value: field.type === "date" ? null : "",
              enabled: false,
              operator: field.type === "number" ? "equals" : "contains",
            };
          }
        });

        return { ...prev, advancedFilters: advanced };
      });
    }
  }, [customFields]);

  const handleAddClient = useCallback(
    async (client: Client) => {
      if (!session?.selectedBusiness?.id) {
        return;
      }
      try {
        await addClient(session.selectedBusiness.id, client);
        await loadClients(1, pagination.pageSize);
      } catch (error) {
        console.error("Error adding client:", error);
      }
    },
    [addClient, session?.selectedBusiness?.id, loadClients, pagination.pageSize]
  );

  const handleUpdateClient = useCallback(
    async (id: string, updates: Partial<Client>) => {
      await updateClient(id, updates);
    },
    [updateClient]
  );

  const handleDeleteClient = useCallback(
    async (id: string) => {
      if (window.confirm("Sei sicuro di voler eliminare questo cliente?")) {
        await deleteClient(id);
      }
    },
    [deleteClient]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilterState);
    loadClients(1, pagination.pageSize);
  }, [pagination.pageSize, loadClients]);

  const handleEnableFilter = useCallback((key: string, enabled: boolean) => {
    setFilters((f) => ({
      ...f,
      advancedFilters: {
        ...f.advancedFilters,
        [key]: { ...f.advancedFilters[key], enabled },
      },
    }));
  }, []);

  const handleChangeFilterValue = useCallback((key: string, value: any) => {
    setFilters((f) => ({
      ...f,
      advancedFilters: {
        ...f.advancedFilters,
        [key]: { ...f.advancedFilters[key], value },
      },
    }));
  }, []);

  const handleChangeFilterOperator = useCallback(
    (key: string, operator: FilterOption["operator"]) => {
      setFilters((f) => ({
        ...f,
        advancedFilters: {
          ...f.advancedFilters,
          [key]: { ...f.advancedFilters[key], operator },
        },
      }));
    },
    []
  );

  const openFilterMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(e.currentTarget);
  }, []);
  const closeFilterMenu = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  // Count active filters
  const activeFilterCount = useMemo(
    () => Object.values(filters.advancedFilters).filter((opt: any) => opt.enabled).length,
    [filters.advancedFilters]
  );

  const sendMassEmail = useCallback(() => {
    setOpen(false);
  }, []);

  // Apply filters to API call
  const applyFilters = useCallback(() => {
    const apiParams: Record<string, any> = {};

    Object.entries(filters.advancedFilters).forEach(([key, filter]) => {
      if (!filter.enabled || !filter.value) return;

      switch (key) {
        case "name":
        case "email":
        case "phone":
        case "source":
        case "customer_group":
          apiParams[key] = filter.value;
          break;

        case "total_spent":
          if (filter.operator === "between" && filter.secondValue) {
            apiParams.total_spent_min = filter.value;
            apiParams.total_spent_max = filter.secondValue;
          } else if (filter.operator === "greaterThan") {
            apiParams.total_spent_min = filter.value;
          } else if (filter.operator === "lessThan") {
            apiParams.total_spent_max = filter.value;
          }
          break;

        case "created":
          if (filter.value) {
            apiParams.created_from = filter.value;
            if (filter.operator === "between" && filter.secondValue) {
              apiParams.created_to = filter.secondValue;
            }
          }
          break;

        case "updated":
          if (filter.value) {
            apiParams.updated_from = filter.value;
            if (filter.operator === "between" && filter.secondValue) {
              apiParams.updated_to = filter.secondValue;
            }
          }
          break;

        case "last_login":
          if (filter.value) {
            apiParams.last_login_from = filter.value;
            if (filter.operator === "between" && filter.secondValue) {
              apiParams.last_login_to = filter.secondValue;
            }
          }
          break;

        case "tags":
          if (filter.value) {
            apiParams.tag = filter.value;
          }
          break;
      }
    });

    return loadClients(1, 25, undefined, apiParams);
  }, [loadClients, filters.advancedFilters]);

  // Handle filter apply
  const handleApplyFilters = useCallback(() => {
    setFilters((prev) => ({ ...prev, showAdvanced: true }));
    applyFilters();
    closeFilterMenu();
  }, [applyFilters, closeFilterMenu]);

  // Render filter field helper
  const renderFilterField = (
    key: string,
    label: string,
    type: "text" | "number" | "select" | "date" | "tags" = "text",
    options?: string[]
  ) => {
    const opt = filters.advancedFilters[key] || {
      value: type === "date" ? null : "",
      enabled: false,
      operator: type === "number" || type === "date" ? "equals" : "contains",
    };
    return (
      <Grid item xs={12} md={6} key={key}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={opt.enabled}
                onChange={(e) => handleEnableFilter(key, e.target.checked)}
              />
            }
            label={label}
            sx={{ minWidth: 150 }}
          />
          {opt.enabled && (
            <Box sx={{ display: "flex", flexGrow: 1, gap: 1 }}>
              {type !== "tags" && (
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    size="small"
                    value={opt.operator}
                    onChange={(e) => handleChangeFilterOperator(key, e.target.value as any)}
                  >
                    <MenuItem value="contains">Contiene</MenuItem>
                    <MenuItem value="equals">Uguale a</MenuItem>
                    {(type === "number" || type === "date") && (
                      <>
                        <MenuItem value="greaterThan">Maggiore di</MenuItem>
                        <MenuItem value="lessThan">Minore di</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              )}
              <Box sx={{ flexGrow: 1 }}>
                {type === "text" && (
                  <TextField
                    size="small"
                    fullWidth
                    value={opt.value || ""}
                    onChange={(e) => handleChangeFilterValue(key, e.target.value)}
                    variant="outlined"
                  />
                )}
                {type === "number" && (
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={opt.value || ""}
                    onChange={(e) => handleChangeFilterValue(key, e.target.value)}
                    variant="outlined"
                  />
                )}
                {type === "select" && options && (
                  <FormControl fullWidth size="small">
                    <Select
                      value={opt.value || ""}
                      onChange={(e) => handleChangeFilterValue(key, e.target.value)}
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      {options.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {type === "date" && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={opt.value ? new Date(opt.value) : null}
                      onChange={(date) => handleChangeFilterValue(key, date)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </LocalizationProvider>
                )}
                {type === "tags" && (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Scrivi tags separati da virgole"
                    value={opt.value || ""}
                    onChange={(e) => handleChangeFilterValue(key, e.target.value)}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Paper elevation={2} sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#37376D!important" }}>
          Database Clienti
        </Typography>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <ClientSearch onSearch={handleSearch} defaultPageSize={pagination.pageSize} />
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={openFilterMenu}
              color={activeFilterCount > 0 ? "primary" : "inherit"}
              endIcon={
                activeFilterCount > 0 ? (
                  <Chip label={activeFilterCount} size="small" color="primary" />
                ) : null
              }
            >
              Filtri
            </Button>
            <Popover
              /* filter popover */
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={closeFilterMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: {
                  width: "70%",
                  maxWidth: "900px",
                  p: 3,
                  bgcolor: "background.paper",
                  boxShadow: 3,
                  borderRadius: 2,
                },
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6">Filtri avanzati</Typography>
                <Button
                  startIcon={<ClearIcon />}
                  onClick={clearAllFilters}
                  color="error"
                  variant="outlined"
                  size="small"
                >
                  Cancella filtri
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderFilterField("name", "Nome")}
                {renderFilterField("email", "Email")}
                {renderFilterField("phone", "Telefono")}
                {renderFilterField("company", "Azienda")}
                {renderFilterField("status", "Stato", "select", ["Lead", "Cliente", "Ex-Cliente"])}
                {renderFilterField("value", "Valore", "number")}
                {renderFilterField("tags", "Tags", "tags")}
                {customFields.map((field) =>
                  renderFilterField(
                    field.id,
                    field.name,
                    field.type,
                    field.type === "select" ? field.options : undefined
                  )
                )}
              </Grid>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleApplyFilters} variant="contained">
                  Applica filtri
                </Button>
              </Box>
            </Popover>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<Email />} onClick={() => setOpen(true)}>
              Manda Mail a tutti
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setCustomFieldsModalOpen(true)}
            >
              Campi Personalizzati
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddModalOpen(true)}
            >
              Aggiungi Cliente
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setBulkImportModalOpen(true)}
            >
              Aggiungi In Massa
            </Button>
            <Button
              variant="contained"
              startIcon={<WhatsApp />}
              onClick={() => navigate("/crm/template")}
            >
              Automazioni
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filter chips summary */}
      {activeFilterCount > 0 && (
        <Box
          sx={{
            p: 1,
            px: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            bgcolor: "grey.100",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {Object.entries(filters.advancedFilters)
            .filter(([, f]) => (f as FilterOption).enabled)
            .map(([key, f]) => {
              const opt = f as FilterOption;
              const nameMap: Record<string, string> = {
                name: "Nome",
                email: "Email",
                phone: "Telefono",
                company: "Azienda",
                status: "Stato",
                value: "Valore",
              };
              const label = nameMap[key] || customFields.find((cf) => cf.id === key)?.name || key;
              const opLabel: Record<string, string> = {
                contains: "contiene",
                equals: "è uguale a",
                greaterThan: "è maggiore di",
                lessThan: "è minore di",
              };
              let displayValue: string = "";
              if (opt.value instanceof Date) {
                displayValue = opt.value.toLocaleDateString();
              } else if (Array.isArray(opt.value)) {
                displayValue = opt.value.join(", ");
              } else {
                displayValue = String(opt.value);
              }

              return (
                <Chip
                  key={key}
                  label={`${label} ${opLabel[opt.operator || "contains"]} ${displayValue}`}
                  onDelete={() => handleEnableFilter(key, false)}
                  size="small"
                />
              );
            })}
          <Chip label="Cancella tutti" onDelete={clearAllFilters} color="error" size="small" />
        </Box>
      )}

      {/* Table component */}
      <GenericTable
        columns={[
          "name",
          "email",
          "phone",
          "company",
          "status",
          "value",
          "tags",
          ...customFields.map((field) => `customField_${field.id}`),
        ]}
        columnLabels={{
          name: "Nome",
          email: "Email",
          phone: "Telefono",
          company: "Azienda",
          status: "Stato",
          value: "Valore",
          tags: "Tag",
          ...Object.fromEntries(
            customFields.map((field) => [`customField_${field.id}`, field.name])
          ),
        }}
        data={clients}
        rowActions={[
          {
            name: "Modifica",
            onClick: (client) => {
              setEditingClient(client);
              setEditModalOpen(true);
            },
          },
          {
            name: "Elimina",
            onClick: (client) => handleDeleteClient(client.id),
          },
        ]}
        noDataMessage={activeFilterCount > 0 ? "Ricerca in corso..." : "Nessun cliente trovato"}
        defineTag="tags"
        pagination={paginationConfig}
        customFields={customFields}
        onRowClick={(client) => {
          setSelectedClientId(client.id);
          setOpenNotesDialog(true);
        }}
        loading={clientsLoading}
      />

      {/* Modals - Only render when they're needed */}
      {addModalOpen && (
        <AddClientModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onAdd={handleAddClient}
          customFields={customFields}
        />
      )}

      {editModalOpen && editingClient && (
        <EditClientModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleUpdateClient}
          client={editingClient}
          customFields={customFields}
        />
      )}

      {customFieldsModalOpen && (
        <CustomFieldsManager
          isOpen={customFieldsModalOpen}
          onClose={() => setCustomFieldsModalOpen(false)}
          customFields={customFields}
          onAdd={addCustomField}
          onUpdate={updateCustomField}
          onDelete={deleteCustomField}
        />
      )}

      {bulkImportModalOpen && (
        <ClientImportModal
          open={bulkImportModalOpen}
          onClose={() => setBulkImportModalOpen(false)}
          onSuccess={async (count) => {
            setBulkImportModalOpen(false);

            // Transform filters into the correct format
            const apiFilters = Object.entries(filters.advancedFilters).reduce(
              (acc, [key, filter]) => {
                if (!filter.enabled || !filter.value) return acc;

                switch (key) {
                  case "email":
                  case "phone":
                  case "source":
                  case "customer_group":
                    return { ...acc, [key]: filter.value };
                  case "total_spent":
                    if (filter.operator === "between" && filter.secondValue) {
                      return {
                        ...acc,
                        total_spent_min: filter.value,
                        total_spent_max: filter.secondValue,
                      };
                    }
                    return acc;
                  case "created":
                  case "updated":
                  case "last_login":
                    if (filter.operator === "between" && filter.secondValue) {
                      return {
                        ...acc,
                        [`${key}_from`]: filter.value,
                        [`${key}_to`]: filter.secondValue,
                      };
                    }
                    return acc;
                  case "tags":
                    return { ...acc, tag: filter.value };
                  default:
                    return acc;
                }
              },
              {}
            );

            try {
              // Refresh both client data and custom fields in parallel
              await Promise.all([
                loadClients(
                  pagination.currentPage,
                  pagination.pageSize,
                  filters.quickSearch || undefined,
                  apiFilters
                ),
                refetch(),
              ]);
            } finally {
              setFilters((prev) => ({ ...prev, showAdvanced: false }));
            }
          }}
          customFields={customFields}
          addCustomFields={addCustomField}
        />
      )}

      <ComposeEmailModal
        open={open}
        onClose={() => setOpen(false)}
        onSend={sendMassEmail}
        displayToandCC={false}
      />
      <ClientNotesModal
        open={openNotesDialog}
        onClose={() => setOpenNotesDialog(false)}
        clientId="0"
      />
    </Paper>
  );
}
